/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {LoadingService} from '../shared/loading/loading-service/loading.service';
import {AboutService} from './about.service';
import {Subscription} from 'rxjs';

@Component({
    selector: 'app-about-page',
    templateUrl: './about.component.html',
    styleUrls: ['./about.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AboutComponent implements OnInit, OnDestroy {

    public specialOrdersVersion: string;
    public specialOrderAdapterVersion: string;
    public claimsVersion: string;
    public claimAdapterVersion: string;
    public securityVersion: string;
    public settingsVersion: string;
    public eventsVersion: string;
    public oauthProxyVersion: string;
    public materialSchedulerVersion: string;
    public materialAdapterVersion: string;
    public guiVersion: string;
    public fileManagerVersion: string;
    public partNumberChangesVersion: string;
    public settlementsVersion: string;
    public bundlesVersion: string;

    private subscriptions = new Subscription();

    private REGEX = '(\\d+)\\.(\\d+)\\.(\\d+)';
    private IGNORE = 'i';

    constructor(private loadingService: LoadingService,
                private aboutPageService: AboutService) {
        loadingService.stop();
    }

    ngOnInit(): void {
        this.getSpecialOrdersVersion();
        this.getSpecialOrderAdapterVersion();
        this.getClaimsVersion();
        this.getClaimAdapterVersion();
        this.getSecurityVersion();
        this.getSettingsVersion();
        this.getEventsVersion();
        this.getOauthProxyVersion();
        this.getMaterialSchedulerVersion();
        this.getMaterialAdapterVersion();
        this.getGuiVersion();
        this.getFileManagerVersion();
        this.getPartNumberChangesVersion();
        this.getSettlementsVersion();
        this.getBundlesVersion();
    }

    /**
     * Gets the specialorders microservice version
     */
    getSpecialOrdersVersion(): void {
        this.subscriptions.add(this.aboutPageService.getSpecialOrdersVersion()
                .subscribe((version: string) => {
                    this.specialOrdersVersion = this.getVersion(version);
                }, (error) => {
                    this.specialOrdersVersion = undefined;
                }));
    }

    /**
     * Gets the ag-specialorder-adapter agent version
     */
    getSpecialOrderAdapterVersion(): void {
        this.subscriptions.add(this.aboutPageService.getSpecialOrderAdapterVersion()
                .subscribe((version: string) => {
                    this.specialOrderAdapterVersion = this.getVersion(version);
                }, (error) => {
                    this.specialOrderAdapterVersion = undefined;
                }));
    }

    /**
     * Gets the claims microservice version
     */
    getClaimsVersion(): void {
        this.subscriptions.add(this.aboutPageService.getClaimsVersion()
                .subscribe((version: string) => {
                    this.claimsVersion = this.getVersion(version);
                }, (error) => {
                    this.claimsVersion = undefined;
                }));
    }

    /**
     * Gets the ag-claim-adapter agent version
     */
    getClaimAdapterVersion(): void {
        this.subscriptions.add(this.aboutPageService.getClaimAdapterVersion()
                .subscribe((version: string) => {
                    this.claimAdapterVersion = this.getVersion(version);
                }, (error) => {
                    this.claimAdapterVersion = undefined;
                }));
    }

    /**
     * Gets the security microservice version
     */
    getSecurityVersion(): void {
        this.subscriptions.add(this.aboutPageService.getSecurityVersion()
                .subscribe((version: string) => {
                    this.securityVersion = this.getVersion(version);
                }, (error) => {
                    this.securityVersion = undefined;
                }));
    }

    /**
     * Gets the settings microservice version
     */
    getSettingsVersion(): void {
        this.subscriptions.add(this.aboutPageService.getSettingsVersion()
                .subscribe((version: string) => {
                    this.settingsVersion = this.getVersion(version);
                }, (error) => {
                    this.settingsVersion = undefined;
                }));
    }

    /**
     * Gets the security microservice version
     */
    getEventsVersion(): void {
        this.subscriptions.add(this.aboutPageService.getEventsVersion()
                .subscribe((version: string) => {
                    this.eventsVersion = this.getVersion(version);
                }, (error) => {
                    this.eventsVersion = undefined;
                }));
    }

    /**
     * Gets the oauth proxy microservice version
     */
    getOauthProxyVersion(): void {
        this.subscriptions.add(this.aboutPageService.getOauthProxyVersion()
                .subscribe((version: string) => {
                    this.oauthProxyVersion = this.getVersion(version);
                }, (error) => {
                    this.oauthProxyVersion = undefined;
                }));
    }

    /**
     * Gets the orcan gui version
     */
    getGuiVersion(): void {
        this.subscriptions.add(this.aboutPageService.getGuiVersion()
                .subscribe((version: string) => {
                    this.guiVersion = this.getVersion(version);
                }, (error) => {
                    this.guiVersion = undefined;
                }));
    }

    /**
     * Gets the ag-material-scheduler microservice version
     */
    getMaterialSchedulerVersion(): void {
        this.subscriptions.add(this.aboutPageService.getMaterialSchedulerVersion()
                .subscribe((version: string) => {
                    this.materialSchedulerVersion = this.getVersion(version);
                }, (error) => {
                    this.materialSchedulerVersion = undefined;
                }));
    }

    /**
     * Gets the ag-material-adapter agent version
     */
    getMaterialAdapterVersion(): void {
        this.subscriptions.add(this.aboutPageService.getMaterialAdapterVersion()
                .subscribe((version: string) => {
                    this.materialAdapterVersion = this.getVersion(version);
                }, (error) => {
                    this.materialAdapterVersion = undefined;
                }));
    }

    /**
     * Gets the filemanager microservice version
     */
    getFileManagerVersion(): void {
        this.subscriptions.add(this.aboutPageService.getFileManagerVersion()
                .subscribe((version: string) => {
                    this.fileManagerVersion = this.getVersion(version);
                }, (error) => {
                    this.fileManagerVersion = undefined;
                }));
    }

    getPartNumberChangesVersion(): void {
        this.subscriptions.add(this.aboutPageService.getPartNumberChangesVersion()
                .subscribe((version: string) => {
                    this.partNumberChangesVersion = this.getVersion(version);
                }, (error) => {
                    this.partNumberChangesVersion = undefined;
                }));
    }

    getSettlementsVersion(): void {
        this.subscriptions.add(this.aboutPageService.getSettlementsVersion()
                .subscribe((version: string) => {
                    this.settlementsVersion = this.getVersion(version);
                }, (error) => {
                    this.settlementsVersion = undefined;
                }));
    }

    getBundlesVersion(): void {
        this.subscriptions.add(this.aboutPageService.getBundlesVersion()
                .subscribe((version: string) => {
                    this.bundlesVersion = this.getVersion(version);
                }, (error) => {
                    this.bundlesVersion = undefined;
                }));
    }

    getVersion(version: string): string {
        return version && version.search(new RegExp(this.REGEX, this.IGNORE)) > -1 ? version : undefined;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

}
